import {
  ActionIcon,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { nameOptionsFilter } from "@dehub/theme"
import { PlusIcon, TrashIcon } from "@phosphor-icons/react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import useSWR from "swr"
import { StaffApi } from "../../apis"
import type { ICustodyExpectedItem } from "../../apis/custody-transfer"
import { IAuth } from "../../store/Auth"
import { message } from "../../utils/message"
import { renderFullName } from "../../utils/render-name"
import { translateBackendError } from "../delivery/translate-error"
import { useCustodyMutations } from "./hooks"

// Manual handover creation. The backend (POST /ord/custody/transfers) creates
// the transfer in DRAFT with a hashed PIN; the same PIN is later supplied at
// sender / receiver verify. Items are optional — receiver-verify reconciles
// quantities against expectedItems.
const TRANSFER_TYPE_VALUES = [
  "WAREHOUSE_TO_CARRIER",
  "CARRIER_TO_CUSTOMER",
  "CARRIER_TO_WAREHOUSE",
  "DRIVER_TO_CASHIER",
  "WAREHOUSE_TO_WAREHOUSE",
  "BUYER_TO_CARRIER",
]

interface ItemRow {
  name: string
  quantity: number | string
  unit: string
}

interface FormValues {
  transferType: string
  fromUserId: string
  toUserId: string
  items: ItemRow[]
}

interface Props {
  opened: boolean
  onClose: () => void
  onCreated: () => void
}

export function CreateHandoverDialog({ opened, onClose, onCreated }: Props) {
  const { t } = useTranslation("delivery")
  const { create, submitting } = useCustodyMutations()
  const businessId = useSelector((s: { auth: IAuth }) => s.auth.user?.currentBusinessId ?? null)

  const transferTypeOptions = TRANSFER_TYPE_VALUES.map((value) => ({
    value,
    label: t(`custody.transferTypeOption.${value}`),
  }))

  const { data: staff } = useSWR(
    businessId && opened ? ["custody-create.staff", businessId] : null,
    () => StaffApi.select({ businessId: businessId as string, limit: 100 }),
    { revalidateOnFocus: false },
  )

  const staffOptions = useMemo(
    () =>
      (staff ?? []).map((s) => ({
        value: s.id,
        label: (s.lastName || s.firstName ? renderFullName(s) : "") || s.id,
      })),
    [staff],
  )

  const form = useForm<FormValues>({
    initialValues: {
      transferType: "WAREHOUSE_TO_CARRIER",
      fromUserId: "",
      toUserId: "",
      items: [],
    },
    validate: {
      transferType: (v) => (v ? null : t("custody.validateType")),
      fromUserId: (v) => (v ? null : t("custody.validateSender")),
      toUserId: (v, values) =>
        !v ? t("custody.validateReceiver") : v === values.fromUserId ? t("custody.validateSameUser") : null,
    },
  })

  const handleClose = () => {
    form.reset()
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    const expectedItems: ICustodyExpectedItem[] = values.items
      .filter((it) => it.name.trim() && Number(it.quantity) > 0)
      .map((it) => ({
        name: it.name.trim(),
        quantity: Number(it.quantity),
        unit: it.unit.trim() || undefined,
      }))

    try {
      await create({
        transferType: values.transferType,
        fromUserId: values.fromUserId,
        toUserId: values.toUserId,
        expectedItems,
      })
      message.success(t("custody.createdSuccess"))
      form.reset()
      onClose()
      onCreated()
    } catch (e) {
      message.error(translateBackendError(e))
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={t("custody.createTitle")} size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          <Select
            label={t("custody.transferTypeLabel")}
            required
            data={transferTypeOptions}
            {...form.getInputProps("transferType")}
          />

          <Group grow align="flex-start">
            <Select
              label={t("custody.sender")}
              placeholder={t("custody.selectPlaceholder")}
              required
              searchable
              filter={nameOptionsFilter}
              nothingFoundMessage={t("custody.notFound")}
              data={staffOptions}
              {...form.getInputProps("fromUserId")}
            />
            <Select
              label={t("custody.receiver")}
              placeholder={t("custody.selectPlaceholder")}
              required
              searchable
              filter={nameOptionsFilter}
              nothingFoundMessage={t("custody.notFound")}
              data={staffOptions}
              {...form.getInputProps("toUserId")}
            />
          </Group>


          <Stack gap={6}>
            <Group justify="space-between" align="center">
              <Text size="sm" fw={600}>
                {t("custody.itemsOptional")}
              </Text>
              <Button
                variant="subtle"
                size="sm"
                leftSection={<PlusIcon size={14} />}
                onClick={() => form.insertListItem("items", { name: "", quantity: 1, unit: "" })}
              >
                {t("custody.addItem")}
              </Button>
            </Group>

            {form.values.items.length === 0 ? (
              <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                {t("custody.noItemsHint")}
              </Text>
            ) : (
              form.values.items.map((_, i) => (
                <Group key={i} align="flex-end" wrap="nowrap" gap="xs">
                  <TextInput
                    label={i === 0 ? t("custody.itemName") : undefined}
                    placeholder={t("custody.itemNamePlaceholder")}
                    style={{ flex: 1 }}
                    {...form.getInputProps(`items.${i}.name`)}
                  />
                  <NumberInput
                    label={i === 0 ? t("custody.itemQty") : undefined}
                    placeholder="0"
                    min={0}
                    w={100}
                    {...form.getInputProps(`items.${i}.quantity`)}
                  />
                  <TextInput
                    label={i === 0 ? t("custody.itemUnit") : undefined}
                    placeholder={t("custody.itemUnitPlaceholder")}
                    w={90}
                    {...form.getInputProps(`items.${i}.unit`)}
                  />
                  <ActionIcon
                    color="gray"
                    variant="subtle"
                    mb={4}
                    onClick={() => form.removeListItem("items", i)}
                    aria-label={t("custody.remove")}
                  >
                    <TrashIcon size={16} />
                  </ActionIcon>
                </Group>
              ))
            )}
          </Stack>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={handleClose} disabled={submitting}>
              {t("custody.cancel")}
            </Button>
            <Button type="submit" loading={submitting}>
              {t("custody.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
